import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Android as AndroidIcon,
  Apple as AppleIcon,
  AutoAwesome as AutoAwesomeIcon,
  Category as CategoryIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  Gavel as GavelIcon,
  Psychology as PsychologyIcon,
  Storefront as StorefrontIcon,
  Tune as TuneIcon,
} from "@mui/icons-material";
import {
  CreateAppDTO,
  GooglePlayConnectionCheckResponse,
} from "../types/app";
import { LegalDocument } from "../types/legal";
import { formatAppCategoryLabel } from "../utils/appCategories";

export type AppRegistrationFormSection = "core" | "store" | "legal" | "advanced";

export interface AdvancedFormData {
  whatItDoes: string;
  targetDemographics: string;
  monetization: string;
  keyFeatures: string;
  growthSignals: string;
  opportunities: string;
  risks: string;
  strategicRecommendations: string;
}

export interface AutoLegalGenerationConfig {
  enabled: boolean;
  isSetupSaved: boolean;
  selectedTypes: Array<LegalDocument["type"]>;
  generationModel: string;
  companyAddress: string;
  contactEmail: string;
  website: string;
  jurisdiction: string;
  appType: "mobile" | "web" | "both";
  dataCollection: {
    personalInfo: string[];
    thirdPartyServices: string[];
    cookies: boolean;
    analytics: boolean;
    advertising: boolean;
  };
  features: {
    userAccounts: boolean;
    inAppPurchases: boolean;
    userGeneratedContent: boolean;
    location: boolean;
    pushNotifications: boolean;
  };
}

interface ModelOption {
  value: string;
  label: string;
}

interface Props {
  aiCopyModel: string;
  aiCopyModels: ModelOption[];
  setAiCopyModel: (value: string) => void;
  formData: CreateAppDTO;
  setFormData: React.Dispatch<React.SetStateAction<CreateAppDTO>>;
  formErrors: Record<string, string>;
  categories: readonly string[];
  generatingNameVariation: boolean;
  generatingDescription: boolean;
  handleGenerateNameVariation: () => void;
  handleGenerateDescription: () => void;
  showAdvancedFields: boolean;
  setShowAdvancedFields: (value: boolean) => void;
  advancedData: AdvancedFormData;
  setAdvancedData: React.Dispatch<React.SetStateAction<AdvancedFormData>>;
  expandedSections: Record<AppRegistrationFormSection, boolean>;
  verifyingGooglePlayConnection: boolean;
  googlePlayConnectionResult: GooglePlayConnectionCheckResponse | null;
  onVerifyGooglePlayConnection: () => void;
  onSectionToggle: (
    section: AppRegistrationFormSection,
  ) => (_event: React.SyntheticEvent, expanded: boolean) => void;
  autoLegalConfig: AutoLegalGenerationConfig;
  setAutoLegalConfig: React.Dispatch<
    React.SetStateAction<AutoLegalGenerationConfig>
  >;
  legalModelOptions: string[];
  legalDocTypeOptions: Array<LegalDocument["type"]>;
  commonPersonalInfoOptions: string[];
  onSaveAutoLegalSetup: () => void;
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 3,
    backgroundColor: "#fff",
  },
};

const STORE_APP_NAME_LIMIT = 30;
const IOS_BUNDLE_ID_REGEX = /^[A-Za-z][A-Za-z0-9-]*(\.[A-Za-z][A-Za-z0-9-]*)+$/;
const ANDROID_PACKAGE_REGEX = /^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/;

const getPlatformTone = (platform: CreateAppDTO["platform"]) => {
  switch (platform) {
    case "android":
      return {
        label: "Android-first",
        color: "#3DDC84",
        bg: "rgba(61,220,132,0.12)",
        icon: <AndroidIcon sx={{ color: "#2ea866" }} />,
      };
    case "ios":
      return {
        label: "iOS-first",
        color: "#1a1a1a",
        bg: "rgba(20,20,20,0.08)",
        icon: <AppleIcon sx={{ color: "#111" }} />,
      };
    default:
      return {
        label: "Cross-platform",
        color: "#1565c0",
        bg: "rgba(21,101,192,0.10)",
        icon: (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <AndroidIcon sx={{ color: "#2ea866", fontSize: 18 }} />
            <AppleIcon sx={{ color: "#111", fontSize: 18 }} />
          </Box>
        ),
      };
  }
};

const getSectionStatus = (
  section: AppRegistrationFormSection,
  formData: CreateAppDTO,
  showAdvancedFields: boolean,
  advancedData: AdvancedFormData,
  autoLegalConfig?: AutoLegalGenerationConfig,
) => {
  if (section === "core") {
    const complete = Boolean(
      formData.name.trim() && formData.description.trim() && formData.category.trim(),
    );
    return complete ? "Complete" : "Required";
  }

  if (section === "store") {
    const hasStoreUrl = Boolean(
      formData.androidStoreUrl?.trim() || formData.iosStoreUrl?.trim(),
    );
    return hasStoreUrl ? "Configured" : "Optional";
  }

  if (section === "legal") {
    if (autoLegalConfig?.enabled) {
      if (autoLegalConfig.isSetupSaved) {
        return `Auto (${autoLegalConfig.selectedTypes.length})`;
      }
      return "Auto (Draft)";
    }
    const count = [
      formData.privacyPolicyUrl?.trim(),
      formData.termsOfServiceUrl?.trim(),
    ].filter(Boolean).length;
    if (count === 2) return "Ready";
    if (count === 1) return "Partial";
    return "Optional";
  }

  if (!showAdvancedFields) return "Disabled";
  const hasContext = Object.values(advancedData).some((value) => value.trim());
  return hasContext ? "Context Added" : "Enabled";
};

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  status: string;
  accent: string;
}> = ({ icon, title, subtitle, status, accent }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      pr: 1,
      gap: 2,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2.5,
          display: "grid",
          placeItems: "center",
          background: accent,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
    </Box>
    <Chip
      size="small"
      label={status}
      sx={{
        borderRadius: 999,
        fontWeight: 700,
        backgroundColor: "#fff",
        border: "1px solid rgba(15,23,42,0.08)",
      }}
    />
  </Box>
);

const AppRegistrationFormSections: React.FC<Props> = ({
  aiCopyModel,
  aiCopyModels,
  setAiCopyModel,
  formData,
  setFormData,
  formErrors,
  categories,
  generatingNameVariation,
  generatingDescription,
  handleGenerateNameVariation,
  handleGenerateDescription,
  showAdvancedFields,
  setShowAdvancedFields,
  advancedData,
  setAdvancedData,
  expandedSections,
  verifyingGooglePlayConnection,
  googlePlayConnectionResult,
  onVerifyGooglePlayConnection,
  onSectionToggle,
  autoLegalConfig,
  setAutoLegalConfig,
  legalModelOptions,
  legalDocTypeOptions,
  commonPersonalInfoOptions,
  onSaveAutoLegalSetup,
}) => {
  const platformTone = getPlatformTone(formData.platform);
  const appNameLength = formData.name.trim().length;
  const appNameOverflow = Math.max(0, appNameLength - STORE_APP_NAME_LIMIT);
  const appNameOverLimit = appNameOverflow > 0;
  const [showStoreAdvanced, setShowStoreAdvanced] = React.useState(false);
  const trim = (value?: string) => String(value || "").trim();
  const hasAndroidTarget =
    formData.platform === "android" || formData.platform === "both";
  const hasIosTarget = formData.platform === "ios" || formData.platform === "both";
  const androidPackage = trim(formData.googlePlayPackageName);
  const androidStoreUrl = trim(formData.androidStoreUrl);
  const iosBundleId = trim(formData.storeId);
  const iosStoreUrl = trim(formData.iosStoreUrl);
  const androidPackageValid = !androidPackage || ANDROID_PACKAGE_REGEX.test(androidPackage);
  const androidStoreUrlValid =
    !androidStoreUrl ||
    /^https?:\/\/play\.google\.com\/store\/apps\/details\?id=/.test(
      androidStoreUrl,
    );
  const iosBundleIdValid = !iosBundleId || IOS_BUNDLE_ID_REGEX.test(iosBundleId);
  const iosStoreUrlValid =
    !iosStoreUrl ||
    /^https?:\/\/apps\.apple\.com\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?app(?:\/[^/]+)?\/id\d+(?:[/?#].*)?$/i.test(
      iosStoreUrl,
    );
  const androidComplete = Boolean(androidPackage && androidStoreUrl && androidStoreUrlValid);
  const iosComplete = Boolean(iosBundleId && iosStoreUrl && iosStoreUrlValid);

  const renderStatusChip = (status: "Missing" | "Valid" | "Complete") => (
    <Chip
      size="small"
      label={status}
      color={status === "Complete" ? "success" : status === "Valid" ? "info" : "default"}
      variant={status === "Missing" ? "outlined" : "filled"}
      sx={{ fontWeight: 700 }}
    />
  );
  const updateAutoLegalConfig = (
    updater: React.SetStateAction<AutoLegalGenerationConfig>,
  ) => {
    setAutoLegalConfig((current) => {
      const next =
        typeof updater === "function"
          ? (
              updater as (
                prevState: AutoLegalGenerationConfig,
              ) => AutoLegalGenerationConfig
            )(current)
          : updater;
      return next.enabled ? { ...next, isSetupSaved: false } : next;
    });
  };

  const getLegalTypeLabel = (type: LegalDocument["type"]) => {
    switch (type) {
      case "privacy":
        return "Privacy Policy";
      case "tos":
        return "Terms of Service";
      case "eula":
        return "End User License Agreement";
      case "cookie":
        return "Cookie Policy";
      case "dpa":
        return "Data Processing Agreement";
      default:
        return type;
    }
  };

  return (
    <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.25,
          borderRadius: 4,
          border: "1px solid rgba(15,23,42,0.08)",
          background:
            "linear-gradient(135deg, rgba(248,250,252,1) 0%, rgba(255,255,255,1) 55%, rgba(240,249,255,1) 100%)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{ mb: 1.25 }}
        >
          <Chip
            icon={<StorefrontIcon />}
            label={platformTone.label}
            sx={{
              fontWeight: 700,
              backgroundColor: platformTone.bg,
              color: platformTone.color,
            }}
          />
          <Chip
            icon={<TuneIcon />}
            label={
              aiCopyModel
                ? `AI: ${
                    aiCopyModels.find((m) => m.value === aiCopyModel)?.label ||
                    aiCopyModel
                  }`
                : "AI copy tools available"
            }
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </Stack>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", lineHeight: 1.7 }}
        >
          Fill the core details first, then add store links and legal URLs.
          Advanced context is optional, but it improves AI outputs across the
          rest of the app.
        </Typography>
      </Paper>

      <Accordion
        expanded={expandedSections.core}
        onChange={onSectionToggle("core")}
        disableGutters
        elevation={0}
        sx={{
          borderRadius: "20px !important",
          border: "1px solid rgba(15,23,42,0.08)",
          overflow: "hidden",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: 2.25, py: 0.75, backgroundColor: "rgba(248,250,252,0.8)" }}
        >
          <SectionHeader
            icon={<DescriptionIcon sx={{ color: "#7c3aed" }} />}
            title="Core App Details"
            subtitle="Identity, category, and AI-assisted copy."
            status={getSectionStatus(
              "core",
              formData,
              showAdvancedFields,
              advancedData,
              autoLegalConfig,
            )}
            accent="rgba(124,58,237,0.10)"
          />
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2.25 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="AI Model For Name & Description"
                value={aiCopyModel}
                onChange={(e) => setAiCopyModel(e.target.value)}
                helperText="Choose a model for the built-in AI name and description actions."
                sx={fieldSx}
              >
                {aiCopyModels.map((option) => (
                  <MenuItem key={option.value || "default"} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="App Name"
                placeholder="TranslatorX Mobile"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                error={!!formErrors.name || appNameOverLimit}
                helperText={
                  formErrors.name ||
                  (appNameOverLimit
                    ? `This name exceeds the 30-character store title guideline by ${appNameOverflow}.`
                    : "Use the public-facing product name. Keep it within 30 characters for store listing safety.")
                }
                FormHelperTextProps={{
                  sx: appNameOverLimit ? { color: "error.main", fontWeight: 600 } : undefined,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Generate AI name variation">
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleGenerateNameVariation}
                            disabled={generatingNameVariation}
                            sx={{
                              borderRadius: 2,
                              backgroundColor: "rgba(124,58,237,0.08)",
                            }}
                          >
                            {generatingNameVariation ? (
                              <CircularProgress size={16} />
                            ) : (
                              <AutoAwesomeIcon
                                fontSize="small"
                                sx={{ color: "#7c3aed" }}
                              />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                required
                sx={fieldSx}
              />
              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  gap: 0.75,
                  flexWrap: "wrap",
                }}
              >
                <Chip
                  size="small"
                  label={`${appNameLength}/${STORE_APP_NAME_LIMIT} characters`}
                  color={appNameOverLimit ? "error" : "default"}
                  variant="outlined"
                />
                {appNameOverLimit && (
                  <Chip
                    size="small"
                    color="error"
                    label={`Reduce by ${appNameOverflow}`}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!formErrors.category}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                  sx={{ borderRadius: 3, backgroundColor: "#fff" }}
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {formatAppCategoryLabel(category)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formErrors.category && (
                <Typography variant="caption" color="error">
                  {formErrors.category}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  height: "100%",
                  borderRadius: 3,
                  border: "1px solid rgba(15,23,42,0.08)",
                  backgroundColor: "rgba(248,250,252,0.72)",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <CategoryIcon sx={{ color: "#475569", fontSize: 18 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Naming Tip
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Keep the name clear and specific. The AI actions work best when
                  the “What It Does” field in Advanced Context is filled in.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="App Description"
                placeholder="Describe the main user value, problem solved, and core functionality."
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, description: e.target.value }))
                }
                error={!!formErrors.description}
                helperText={
                  formErrors.description || "Used across legal and AI-assisted features."
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title='Generate AI description from "What It Does"'>
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleGenerateDescription}
                            disabled={generatingDescription}
                            sx={{
                              borderRadius: 2,
                              backgroundColor: "rgba(14,165,233,0.10)",
                            }}
                          >
                            {generatingDescription ? (
                              <CircularProgress size={16} />
                            ) : (
                              <AutoAwesomeIcon
                                fontSize="small"
                                sx={{ color: "#0284c7" }}
                              />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                required
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expandedSections.store}
        onChange={onSectionToggle("store")}
        disableGutters
        elevation={0}
        sx={{
          borderRadius: "20px !important",
          border: "1px solid rgba(15,23,42,0.08)",
          overflow: "hidden",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: 2.25, py: 0.75, backgroundColor: "rgba(248,250,252,0.8)" }}
        >
          <SectionHeader
            icon={platformTone.icon}
            title="Platform & Store Details"
            subtitle="Platform targeting, app ID, and store links."
            status={getSectionStatus(
              "store",
              formData,
              showAdvancedFields,
              advancedData,
              autoLegalConfig,
            )}
            accent={platformTone.bg}
          />
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2.25 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Platform
              </Typography>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                {[
                  { value: "android", label: "Android", icon: <AndroidIcon sx={{ color: "#3DDC84" }} /> },
                  { value: "ios", label: "iOS", icon: <AppleIcon /> },
                  {
                    value: "both",
                    label: "Both",
                    icon: (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <AndroidIcon sx={{ color: "#3DDC84", fontSize: 18 }} />
                        <AppleIcon sx={{ fontSize: 18 }} />
                      </Box>
                    ),
                  },
                ].map((item) => (
                  <Paper
                    key={item.value}
                    elevation={0}
                    role="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        platform: item.value as "android" | "ios" | "both",
                      }))
                    }
                    sx={{
                      p: 1.25,
                      borderRadius: 2.5,
                      border: "1px solid",
                      borderColor:
                        formData.platform === item.value
                          ? "primary.main"
                          : "rgba(15,23,42,0.12)",
                      bgcolor:
                        formData.platform === item.value
                          ? "rgba(37,99,235,0.08)"
                          : "#fff",
                      cursor: "pointer",
                      minWidth: 140,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      {item.icon}
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {item.label}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  height: "100%",
                  borderRadius: 3,
                  border: "1px solid rgba(15,23,42,0.08)",
                  backgroundColor: platformTone.bg,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  {platformTone.icon}
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {platformTone.label}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {formData.platform === "both"
                    ? "Add both store URLs so the rest of the platform can reference the right destination per store."
                    : `Use the ${
                        formData.platform === "android" ? "Google Play" : "App Store"
                      } URL as the primary source of truth for this app.`}
                </Typography>
              </Paper>
            </Grid>
            {hasAndroidTarget && (
              <Grid item xs={12} md={hasIosTarget ? 6 : 12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    border: "1px solid rgba(61,220,132,0.30)",
                    backgroundColor: "rgba(61,220,132,0.06)",
                    height: "100%",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AndroidIcon sx={{ color: "#2ea866" }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Android Store Details
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75}>
                      {(androidPackage || androidStoreUrl) && (
                        <Chip size="small" label="Detected" variant="outlined" />
                      )}
                      {renderStatusChip(
                        androidComplete
                          ? "Complete"
                          : androidStoreUrlValid && androidPackageValid && (androidPackage || androidStoreUrl)
                          ? "Valid"
                          : "Missing",
                      )}
                    </Stack>
                  </Stack>
                  <TextField
                    fullWidth
                    label="Package Name"
                    placeholder="com.example.app"
                    value={formData.googlePlayPackageName || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        googlePlayPackageName: e.target.value,
                      }))
                    }
                    error={!!formErrors.googlePlayPackageName}
                    color={
                      androidPackage && androidPackageValid && !formErrors.googlePlayPackageName
                        ? "success"
                        : "primary"
                    }
                    helperText={
                      formErrors.googlePlayPackageName ||
                      "Example: com.company.product"
                    }
                    sx={{ ...fieldSx, mb: 1.25 }}
                  />
                  <TextField
                    fullWidth
                    label="Play Store URL"
                    placeholder="https://play.google.com/store/apps/details?id=com.example.app"
                    value={formData.androidStoreUrl || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        androidStoreUrl: e.target.value,
                        storeUrl:
                          prev.platform === "android" ? e.target.value : prev.storeUrl,
                      }))
                    }
                    error={!!formErrors.androidStoreUrl}
                    color={
                      androidStoreUrl && androidStoreUrlValid && !formErrors.androidStoreUrl
                        ? "success"
                        : "primary"
                    }
                    helperText={
                      formErrors.androidStoreUrl ||
                      "Accepted: https://play.google.com/store/apps/details?id=..."
                    }
                    sx={fieldSx}
                  />
                </Paper>
              </Grid>
            )}
            {hasIosTarget && (
              <Grid item xs={12} md={hasAndroidTarget ? 6 : 12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    border: "1px solid rgba(17,24,39,0.20)",
                    backgroundColor: "rgba(17,24,39,0.03)",
                    height: "100%",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AppleIcon />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        iOS Store Details
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75}>
                      {(iosBundleId || iosStoreUrl) && (
                        <Chip size="small" label="Detected" variant="outlined" />
                      )}
                      {renderStatusChip(
                        iosComplete
                          ? "Complete"
                          : iosStoreUrlValid && iosBundleIdValid && (iosBundleId || iosStoreUrl)
                          ? "Valid"
                          : "Missing",
                      )}
                    </Stack>
                  </Stack>
                  <TextField
                    fullWidth
                    label="Bundle Identifier (iOS)"
                    placeholder="com.company.app"
                    value={formData.storeId}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, storeId: e.target.value }))
                    }
                    error={!!formErrors.storeId}
                    color={
                      iosBundleId && iosBundleIdValid && !formErrors.storeId
                        ? "success"
                        : "primary"
                    }
                    helperText={
                      formErrors.storeId ||
                      "App Store Connect bundle ID. Example: com.company.app"
                    }
                    sx={{ ...fieldSx, mb: 1.25 }}
                  />
                  <TextField
                    fullWidth
                    label="App Store URL"
                    placeholder="https://apps.apple.com/app/id1234567890"
                    value={formData.iosStoreUrl || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        iosStoreUrl: e.target.value,
                        storeUrl:
                          prev.platform === "ios" ? e.target.value : prev.storeUrl,
                      }))
                    }
                    error={!!formErrors.iosStoreUrl}
                    color={
                      iosStoreUrl && iosStoreUrlValid && !formErrors.iosStoreUrl
                        ? "success"
                        : "primary"
                    }
                    helperText={
                      formErrors.iosStoreUrl ||
                      "Accepted: https://apps.apple.com/app/id123 and .../app/name/id123"
                    }
                    sx={fieldSx}
                  />
                </Paper>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                size="small"
                variant="text"
                onClick={() => setShowStoreAdvanced((prev) => !prev)}
              >
                {showStoreAdvanced ? "Hide Advanced Store Settings" : "Show Advanced Store Settings"}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Collapse in={showStoreAdvanced}>
                <Grid container spacing={2}>
                  {hasAndroidTarget && (
                    <Grid item xs={12} md={5}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          height: "100%",
                          borderRadius: 3,
                          border: "1px solid rgba(15,23,42,0.08)",
                          backgroundColor: "#fff",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Verify Google Play Connection
                          </Typography>
                          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                            Checks whether the configured Play service account can access this package.
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          onClick={onVerifyGooglePlayConnection}
                          disabled={
                            verifyingGooglePlayConnection ||
                            !formData.googlePlayPackageName?.trim()
                          }
                        >
                          {verifyingGooglePlayConnection ? (
                            <CircularProgress size={20} />
                          ) : (
                            "Verify Connection"
                          )}
                        </Button>
                      </Paper>
                    </Grid>
                  )}
                  <Grid item xs={12} md={hasAndroidTarget ? 7 : 12}>
                    <TextField
                      fullWidth
                      label="Firebase Project ID (Optional)"
                      placeholder="my-app-prod"
                      value={formData.firebaseProjectId || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          firebaseProjectId: e.target.value,
                        }))
                      }
                      error={!!formErrors.firebaseProjectId}
                      helperText={
                        formErrors.firebaseProjectId ||
                        "Required for App Engagement campaigns (FCM API targeting)."
                      }
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="GA4 Property ID (Optional)"
                      placeholder="123456789"
                      value={formData.ga4PropertyId || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          ga4PropertyId: e.target.value,
                        }))
                      }
                      error={!!formErrors.ga4PropertyId}
                      helperText={
                        formErrors.ga4PropertyId ||
                        "Required for App Growth. Add the backend service account as a Viewer on this GA4 property."
                      }
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="GA4 Stream ID (Optional)"
                      placeholder="1234567890"
                      value={formData.ga4StreamId || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          ga4StreamId: e.target.value,
                        }))
                      }
                      helperText="Use this when multiple apps share one GA4 property."
                      sx={fieldSx}
                    />
                  </Grid>
                  {googlePlayConnectionResult && (
                    <Grid item xs={12}>
                      <Alert
                        severity={
                          googlePlayConnectionResult.success ? "success" : "error"
                        }
                      >
                        {googlePlayConnectionResult.message}
                        {googlePlayConnectionResult.packageName
                          ? ` (${googlePlayConnectionResult.packageName})`
                          : ""}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Collapse>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expandedSections.legal}
        onChange={onSectionToggle("legal")}
        disableGutters
        elevation={0}
        sx={{
          borderRadius: "20px !important",
          border: "1px solid rgba(15,23,42,0.08)",
          overflow: "hidden",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: 2.25, py: 0.75, backgroundColor: "rgba(248,250,252,0.8)" }}
        >
          <SectionHeader
            icon={<GavelIcon sx={{ color: "#b45309" }} />}
            title="Legal Links"
            subtitle="Required destinations for compliant listings."
            status={getSectionStatus(
              "legal",
              formData,
              showAdvancedFields,
              advancedData,
              autoLegalConfig,
            )}
            accent="rgba(245,158,11,0.12)"
          />
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2.25 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  border: "1px solid rgba(245,158,11,0.18)",
                  backgroundColor: "rgba(255,251,235,0.8)",
                }}
              >
                <Typography variant="body2" sx={{ color: "#92400e" }}>
                  Add legal URLs manually, or enable auto-generation to create legal
                  documents as part of app creation and attach links automatically.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  border: "1px solid rgba(15,23,42,0.08)",
                  backgroundColor: autoLegalConfig.enabled
                    ? "rgba(236,253,245,0.8)"
                    : "rgba(248,250,252,0.8)",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Auto-generate legal documents during app creation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enable this to configure legal generation now and attach live links
                      when the app is created.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    sx={{ mr: 0 }}
                    control={
                      <Switch
                        checked={autoLegalConfig.enabled}
                        onChange={(event) =>
                          updateAutoLegalConfig((prev) => ({
                            ...prev,
                            enabled: event.target.checked,
                            isSetupSaved: !event.target.checked
                              ? false
                              : prev.isSetupSaved,
                          }))
                        }
                      />
                    }
                    label={autoLegalConfig.enabled ? "Enabled" : "Disabled"}
                  />
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Collapse in={autoLegalConfig.enabled}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: "1px solid rgba(16,185,129,0.25)",
                    backgroundColor: "#fff",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                        Document Types
                      </Typography>
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          p: 1,
                          backgroundColor: "action.hover",
                        }}
                      >
                        {legalDocTypeOptions.map((type) => (
                          <FormControlLabel
                            key={type}
                            control={
                              <Checkbox
                                checked={autoLegalConfig.selectedTypes.includes(type)}
                                onChange={() =>
                                  updateAutoLegalConfig((prev) => ({
                                    ...prev,
                                    selectedTypes: prev.selectedTypes.includes(type)
                                      ? prev.selectedTypes.filter((item) => item !== type)
                                      : [...prev.selectedTypes, type],
                                  }))
                                }
                              />
                            }
                            label={getLegalTypeLabel(type)}
                          />
                        ))}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="Generation Model"
                        value={autoLegalConfig.generationModel}
                        onChange={(event) =>
                          updateAutoLegalConfig((prev) => ({
                            ...prev,
                            generationModel: event.target.value,
                          }))
                        }
                        sx={fieldSx}
                      >
                        {legalModelOptions.map((modelName) => (
                          <MenuItem key={modelName} value={modelName}>
                            {modelName}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Company Name"
                        value={formData.name || ""}
                        helperText="Company name uses the app name automatically."
                        InputProps={{ readOnly: true }}
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Contact Email"
                        value={autoLegalConfig.contactEmail}
                        onChange={(event) =>
                          updateAutoLegalConfig((prev) => ({
                            ...prev,
                            contactEmail: event.target.value,
                          }))
                        }
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Company Address (Optional)"
                        value={autoLegalConfig.companyAddress}
                        onChange={(event) =>
                          updateAutoLegalConfig((prev) => ({
                            ...prev,
                            companyAddress: event.target.value,
                          }))
                        }
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Website (Optional)"
                        value={autoLegalConfig.website}
                        onChange={(event) =>
                          updateAutoLegalConfig((prev) => ({
                            ...prev,
                            website: event.target.value,
                          }))
                        }
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="Jurisdiction"
                        value={autoLegalConfig.jurisdiction}
                        onChange={(event) =>
                          updateAutoLegalConfig((prev) => ({
                            ...prev,
                            jurisdiction: event.target.value,
                          }))
                        }
                        sx={fieldSx}
                      >
                        {["US", "EU", "UK", "CA", "AU", "Global"].map((value) => (
                          <MenuItem key={value} value={value}>
                            {value}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="App Type"
                        value={autoLegalConfig.appType}
                        onChange={(event) =>
                          updateAutoLegalConfig((prev) => ({
                            ...prev,
                            appType: event.target.value as "mobile" | "web" | "both",
                          }))
                        }
                        sx={fieldSx}
                      >
                        <MenuItem value="mobile">Mobile</MenuItem>
                        <MenuItem value="web">Web</MenuItem>
                        <MenuItem value="both">Both</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth sx={fieldSx}>
                        <InputLabel>Personal Information Collected</InputLabel>
                        <Select
                          multiple
                          label="Personal Information Collected"
                          value={autoLegalConfig.dataCollection.personalInfo}
                          renderValue={(selected) =>
                            (selected as string[]).join(", ")
                          }
                          onChange={(event) => {
                            const value = event.target.value as string[];
                            updateAutoLegalConfig((prev) => ({
                              ...prev,
                              dataCollection: {
                                ...prev.dataCollection,
                                personalInfo: value,
                              },
                            }));
                          }}
                        >
                          {commonPersonalInfoOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              <Checkbox
                                checked={autoLegalConfig.dataCollection.personalInfo.includes(
                                  option,
                                )}
                              />
                              <ListItemText primary={option} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Third-Party Services"
                        placeholder="Firebase, Stripe, Google Analytics"
                        value={autoLegalConfig.dataCollection.thirdPartyServices.join(", ")}
                        onChange={(event) =>
                          updateAutoLegalConfig((prev) => ({
                            ...prev,
                            dataCollection: {
                              ...prev.dataCollection,
                              thirdPartyServices: event.target.value
                                .split(",")
                                .map((item) => item.trim())
                                .filter(Boolean),
                            },
                          }))
                        }
                        helperText="Comma-separated list"
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                        Data Collection Flags
                      </Typography>
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          p: 1,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={autoLegalConfig.dataCollection.cookies}
                              onChange={(event) =>
                                updateAutoLegalConfig((prev) => ({
                                  ...prev,
                                  dataCollection: {
                                    ...prev.dataCollection,
                                    cookies: event.target.checked,
                                  },
                                }))
                              }
                            />
                          }
                          label="Uses Cookies"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={autoLegalConfig.dataCollection.analytics}
                              onChange={(event) =>
                                updateAutoLegalConfig((prev) => ({
                                  ...prev,
                                  dataCollection: {
                                    ...prev.dataCollection,
                                    analytics: event.target.checked,
                                  },
                                }))
                              }
                            />
                          }
                          label="Uses Analytics"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={autoLegalConfig.dataCollection.advertising}
                              onChange={(event) =>
                                updateAutoLegalConfig((prev) => ({
                                  ...prev,
                                  dataCollection: {
                                    ...prev.dataCollection,
                                    advertising: event.target.checked,
                                  },
                                }))
                              }
                            />
                          }
                          label="Uses Advertising"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                        App Features
                      </Typography>
                      <Grid container spacing={1}>
                        {[
                          ["userAccounts", "User Accounts"],
                          ["inAppPurchases", "In-App Purchases"],
                          ["userGeneratedContent", "User Generated Content"],
                          ["location", "Location Features"],
                          ["pushNotifications", "Push Notifications"],
                        ].map(([key, label]) => (
                          <Grid item xs={12} sm={6} md={4} key={key}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    autoLegalConfig.features[
                                      key as keyof AutoLegalGenerationConfig["features"]
                                    ]
                                  }
                                  onChange={(event) =>
                                    updateAutoLegalConfig((prev) => ({
                                      ...prev,
                                      features: {
                                        ...prev.features,
                                        [key]: event.target.checked,
                                      },
                                    }))
                                  }
                                />
                              }
                              label={label}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.25}
                        alignItems={{ xs: "stretch", sm: "center" }}
                        justifyContent="space-between"
                      >
                        <Typography variant="body2" color="text.secondary">
                          Save this legal setup to collapse and continue app creation.
                        </Typography>
                        <Button variant="contained" onClick={onSaveAutoLegalSetup}>
                          Save Legal Setup
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              </Collapse>
            </Grid>

            {!autoLegalConfig.enabled && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Privacy Policy URL"
                    placeholder="https://example.com/privacy"
                    value={formData.privacyPolicyUrl || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        privacyPolicyUrl: e.target.value,
                      }))
                    }
                    error={!!formErrors.privacyPolicyUrl}
                    helperText={
                      formErrors.privacyPolicyUrl || "Recommended for both stores."
                    }
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Terms of Service URL"
                    placeholder="https://example.com/terms"
                    value={formData.termsOfServiceUrl || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        termsOfServiceUrl: e.target.value,
                      }))
                    }
                    error={!!formErrors.termsOfServiceUrl}
                    helperText={
                      formErrors.termsOfServiceUrl ||
                      "Useful for subscriptions or account-based apps."
                    }
                    sx={fieldSx}
                  />
                </Grid>
              </>
            )}
            {formErrors.autoLegalConfig && (
              <Grid item xs={12}>
                <Alert severity="error">{formErrors.autoLegalConfig}</Alert>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expandedSections.advanced}
        onChange={onSectionToggle("advanced")}
        disableGutters
        elevation={0}
        sx={{
          borderRadius: "20px !important",
          border: "1px solid rgba(15,23,42,0.08)",
          overflow: "hidden",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: 2.25, py: 0.75, backgroundColor: "rgba(248,250,252,0.8)" }}
        >
          <SectionHeader
            icon={<PsychologyIcon sx={{ color: "#0f766e" }} />}
            title="Advanced Context"
            subtitle="Optional research data for stronger AI output quality."
            status={getSectionStatus(
              "advanced",
              formData,
              showAdvancedFields,
              advancedData,
              autoLegalConfig,
            )}
            accent="rgba(13,148,136,0.12)"
          />
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2.25 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  border: "1px solid rgba(15,23,42,0.08)",
                  backgroundColor: showAdvancedFields
                    ? "rgba(236,253,245,0.8)"
                    : "rgba(248,250,252,0.8)",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                      Research Context
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enable this when you want better AI suggestions for listings,
                      marketing, and related workflows.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    sx={{ mr: 0 }}
                    control={
                      <Switch
                        checked={showAdvancedFields}
                        onChange={(event) =>
                          setShowAdvancedFields(event.target.checked)
                        }
                      />
                    }
                    label={showAdvancedFields ? "Enabled" : "Disabled"}
                  />
                </Stack>
              </Paper>
            </Grid>

            {showAdvancedFields && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="What It Does"
                    placeholder="Summarize the core user problem and how the app solves it."
                    value={advancedData.whatItDoes}
                    onChange={(e) =>
                      setAdvancedData((prev) => ({
                        ...prev,
                        whatItDoes: e.target.value,
                      }))
                    }
                    helperText="Most important field for AI-generated names and descriptions."
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Target Demographics"
                    value={advancedData.targetDemographics}
                    onChange={(e) =>
                      setAdvancedData((prev) => ({
                        ...prev,
                        targetDemographics: e.target.value,
                      }))
                    }
                    helperText="One per line or comma-separated"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Monetization"
                    value={advancedData.monetization}
                    onChange={(e) =>
                      setAdvancedData((prev) => ({
                        ...prev,
                        monetization: e.target.value,
                      }))
                    }
                    helperText="One per line or comma-separated"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Key Features"
                    value={advancedData.keyFeatures}
                    onChange={(e) =>
                      setAdvancedData((prev) => ({
                        ...prev,
                        keyFeatures: e.target.value,
                      }))
                    }
                    helperText="One per line or comma-separated"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Growth Signals"
                    value={advancedData.growthSignals}
                    onChange={(e) =>
                      setAdvancedData((prev) => ({
                        ...prev,
                        growthSignals: e.target.value,
                      }))
                    }
                    helperText="One per line or comma-separated"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Opportunities"
                    value={advancedData.opportunities}
                    onChange={(e) =>
                      setAdvancedData((prev) => ({
                        ...prev,
                        opportunities: e.target.value,
                      }))
                    }
                    helperText="One per line or comma-separated"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Risks"
                    value={advancedData.risks}
                    onChange={(e) =>
                      setAdvancedData((prev) => ({
                        ...prev,
                        risks: e.target.value,
                      }))
                    }
                    helperText="One per line or comma-separated"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Strategic Recommendations"
                    value={advancedData.strategicRecommendations}
                    onChange={(e) =>
                      setAdvancedData((prev) => ({
                        ...prev,
                        strategicRecommendations: e.target.value,
                      }))
                    }
                    helperText="One per line or comma-separated"
                    sx={fieldSx}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default AppRegistrationFormSections;
